import { X } from 'lucide-react'
import { cn } from '../../lib/cn.js'

export interface ChipProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onSelect'> {
  selected?: boolean
  /** When given, the chip carries a cross and this is what it does. */
  onRemove?: () => void
}

/**
 * A narrow-it-down control that is pressed rather than read.
 *
 * Distinct from a badge, which is a word about a row and is never pressable. The
 * difference matters: somebody who learns a coloured pill is a button will press
 * one that is not.
 */
export function Chip({ className, selected, onRemove, children, ...props }: ChipProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border text-second transition-colors',
        selected
          ? 'border-coral-fill bg-coral-soft text-coral-text'
          : 'border-hairline-strong bg-surface text-ink-2 hover:bg-sunk',
        className,
      )}
    >
      <button
        type="button"
        aria-pressed={selected}
        className="rounded-full px-3 py-1 outline-none focus-visible:ring-2 focus-visible:ring-coral-fill/30"
        {...props}
      >
        {children}
      </button>
      {onRemove && (
        <button
          type="button"
          aria-label="Remove"
          onClick={onRemove}
          className="pr-2 outline-none focus-visible:ring-2 focus-visible:ring-coral-fill/30 [&_svg]:size-3"
        >
          <X />
        </button>
      )}
    </span>
  )
}
